import { AUTO_TOC_NODE_ID, TreeDataNode } from '@livv/models';
import { getBookLink, getNodeLink } from '@livv/utils/helpers';
import { useRouter } from 'next/router';
import { FC } from 'react';
import TreeItems from '@components/Tree/TreeItems';
import { useBookPageContext } from '@utils/context/book';

interface CustomTreeItemsProps {
    nodes: TreeDataNode[];
    parentPath?: string;
    searchTerm?: string;
    treeId: string;
    withLevelPrefix?: boolean;
}

const CustomTreeItems: FC<CustomTreeItemsProps> = ({
    treeId,
    nodes,
    parentPath,
    searchTerm,
    withLevelPrefix,
}) => {
    const { bookData, bookMeta } = useBookPageContext();
    const router = useRouter();

    const handleNodeSelection = (path: string) => {
        if (path === AUTO_TOC_NODE_ID) {
            const redirectionLink = getBookLink({ bookId: treeId });

            if (router.asPath !== redirectionLink) {
                router.push(redirectionLink);
            }

            return;
        }

        const redirectionLink = getNodeLink({ bookId: treeId, bookPath: path });
        if (router.asPath !== redirectionLink) {
            router.push(getNodeLink({ bookId: treeId, bookPath: path }));
        }
    };

    const generateLabel = (title: string, path: string) => {
        const titleWithHighlightedMatches =
            searchTerm && title
                ? title.replace(
                      new RegExp(`(${searchTerm})`, 'ig'),
                      `<span class="searchHighlight">$1</span>`,
                  )
                : title;

        if (path === AUTO_TOC_NODE_ID) {
            return (
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: titleWithHighlightedMatches,
                    }}
                />
            );
        }

        const typo = bookData.getLocalNodeTypography(
            path.split('/'),
            bookMeta.language,
            bookMeta.dummyLevel,
        );
        const displayTypo = withLevelPrefix && Boolean(typo);

        return (
            <>
                {displayTypo && (
                    <span>
                        <strong>{bookMeta.language === 'FR' ? `${typo} : ` : `${typo}: `}</strong>
                    </span>
                )}
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: titleWithHighlightedMatches,
                    }}
                />
            </>
        );
    };

    return (
        <TreeItems
            generateLabel={generateLabel}
            nodes={nodes}
            onNodeSelection={handleNodeSelection}
            parentPath={parentPath}
        />
    );
};

export default CustomTreeItems;
